import Joi from 'joi';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useContext, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';
import { SextforceAutoRepostLog } from './useSextforceAutoRepostLogs';

export interface SextforceAutoRepostColor {
    _id?: string;
    subscriberId: string;
    name: string;
    color: string;
}

export const schemaSextforceAutoRepostColor = Joi.object<SextforceAutoRepostColor>({
    subscriberId: Joi.string().required(),
    name: Joi.string().trim().required().default('').messages({
        'string.empty': 'The Name cannot be empty.',
        'string.default': 'The Name cannot be empty.',
    }),
    color: Joi.string().trim().required().default('').messages({
        'string.empty': 'The Color cannot be empty.',
        'string.default': 'The Color cannot be empty.',
    }),
});

// Array of all possible values for the SextforceAutoRepostActionPreviousPost type
export type SextforceAutoRepostAdjustUnit = 'minutes' | 'hours' | 'days' | 'weeks';

export const arraySextforceAutoRepostActionPreviousPost = ['none', 'delete', 'archive', 'privateArchive'];

// Type for the SextforceAutoRepostActionPreviousPost
export type SextforceAutoRepostActionPreviousPost = typeof arraySextforceAutoRepostActionPreviousPost[number];

export const arraySextforceAutoRepostActionPreviousPostDisplay = ['Nothing', 'Delete', 'Archive', 'Private Archive'];

export const arraySextforceAutoRepostType = ['link', 'post', 'album', 'template', 'caption'];

export type SextforceAutoRepostType = typeof arraySextforceAutoRepostType[number];

export const postUrlForAutoReply = (subscriber: any, autoRepost: SextforceAutoRepostWithLatestRepostLog) => {
    if (autoRepost.type === 'link') {
        return autoRepost.originalPostUrl;
    }

    if (autoRepost.type === 'post') {
        if (!autoRepost.latestRepostLog) {
            return autoRepost.originalPostUrl;
        }

        const lastPostId = autoRepost.latestRepostLog.postId;
        const newPostUrl = `https://onlyfans.com/${lastPostId}/${subscriber.username}`;

        return newPostUrl;
    }

    if (autoRepost.type === 'album') {
        return undefined;
    }

    if (autoRepost.type === 'template') {
        return undefined;
    }

    if (autoRepost.type === 'caption') {
        return undefined;
    }
};

export interface SextforceAutoRepost {
    _id?: string;
    subscriberId: string;
    name: string;
    description: string;
    colorId?: string;
    active: boolean;
    inactive?: boolean;
    originalPostUrl: string;
    type: SextforceAutoRepostType;
    startDate?: Date;
    endDate?: Date;
    repostEveryAmount: number;
    repostEveryUnit: moment.DurationInputArg2;
    repostWhenCampaignHitsTarget?: boolean;
    pinPost: boolean;
    repostCount: number;
    privateArchiveAfterActive?: boolean;
    privateArchiveAfterAmount?: number;
    privateArchiveAfterUnit?: string;
    actionPreviousPost: SextforceAutoRepostActionPreviousPost;
    nextRepostDate: Date;
    latestRepostDate?: Date;
    deletedDate?: Date;
    hasErrors?: boolean;
    isPostTipCampaign?: boolean;
    repostAfterTipCount?: number;
    postId?: number;
    sourceAlbumId?: number;
    postCaption?: boolean;
    captionCategory?: string;
    postImage?: boolean;
    postVideo?: boolean;
    postAudio?: boolean;
    postGif?: boolean;
    templateIds?: Array<{
        inventoryId: number;
        versionId: number;
    }>;
    /** Template mode: tag pool resolved at post time (mutually exclusive with templateIds) */
    templateTags?: string[];
    /** Tags mode: only include templates with a price (default true) */
    templateTagsPaidOnly?: boolean;
}

export const schemaSextforceAutoRepost = Joi.object<SextforceAutoRepost>({
    subscriberId: Joi.string().required(),
    originalPostUrl: Joi.when('type', {
        is: Joi.valid('album', 'template', 'caption'),
        then: Joi.string().trim().optional().allow(''),
        otherwise: Joi.string().trim().required().uri().messages({
            'string.uri': 'The Original Post URL must be a valid URL.',
            'string.empty': 'The Original Post URL cannot be empty.',
        }),
    }),
    name: Joi.string().trim().required().default('').messages({
        'string.empty': 'The Name cannot be empty.',
        'string.default': 'The Name cannot be empty.',
    }),
    description: Joi.string().trim().optional().allow(''),
    colorId: Joi.string().optional().allow(''),
    active: Joi.boolean().required(),
    inactive: Joi.boolean().optional().default(false),
    type: Joi.string()
        .valid(...arraySextforceAutoRepostType)
        .required(),
    startDate: Joi.date().optional(),
    endDate: Joi.date().optional(),
    repostEveryAmount: Joi.number()
        .min(1)
        .required()
        .messages({
            'number.min': 'The Repost Every Amount must be at least 10 minutes.',
            'number.base': 'The Repost Every Amount must be a number.',
            'number.empty': 'The Repost Every Amount cannot be empty.',
            'number.default': 'The Repost Every Amount cannot be empty.',
        })
        .custom((value, helpers) => {
            // Check that the amount is no less than 10 when repostEveryUnit is set to 'minutes'
            if (value < 10 && helpers.state.ancestors[0].repostEveryUnit === 'minutes') {
                return helpers.error('number.min');
            }

            return true;
        }),
    repostEveryUnit: Joi.string().required(),
    repostWhenCampaignHitsTarget: Joi.boolean().optional().default(false),
    pinPost: Joi.boolean().required().default(false),
    repostCount: Joi.number().required(),
    privateArchiveAfterActive: Joi.boolean().optional().default(false),
    privateArchiveAfterAmount: Joi.number().when('privateArchiveAfterActive', {
        is: true,
        then: Joi.number().min(1).required().messages({
            'number.min': 'The Private Archive After Amount must be at least 1.',
            'number.base': 'The Private Archive After Amount must be a number.',
            'number.empty': 'The Private Archive After Amount cannot be empty.',
            'number.default': 'The Private Archive After Amount cannot be empty.',
        }),
        otherwise: Joi.number().optional(),
    }),
    privateArchiveAfterUnit: Joi.string().when('privateArchiveAfterActive', {
        is: true,
        then: Joi.string().required(),
        otherwise: Joi.string().optional(),
    }),
    actionPreviousPost: Joi.string()
        .valid(...arraySextforceAutoRepostActionPreviousPost)
        .required(),
    nextRepostDate: Joi.date().required(),
    latestRepostDate: Joi.date().optional(),
    deletedDate: Joi.date().optional(),
    hasErrors: Joi.boolean().optional().default(false),
    isPostTipCampaign: Joi.boolean().optional().default(false),
    repostAfterTipCount: Joi.number().optional(),
    postId: Joi.number().optional(),
    sourceAlbumId: Joi.number().optional(),
    postCaption: Joi.boolean().optional().default(false),
    captionCategory: Joi.string().trim().optional().allow('').default(''),
    postImage: Joi.boolean().optional().default(false),
    postVideo: Joi.boolean().optional().default(false),
    postAudio: Joi.boolean().optional().default(false),
    postGif: Joi.boolean().optional().default(false),
    templateIds: Joi.when('type', {
        is: 'template',
        then: Joi.when('templateTags', {
            is: Joi.array().min(1).required(),
            then: Joi.array()
                .items(
                    Joi.object({
                        inventoryId: Joi.number().required(),
                        versionId: Joi.number().required(),
                    }),
                )
                .optional()
                .default([]),
            otherwise: Joi.array()
                .items(
                    Joi.object({
                        inventoryId: Joi.number().required(),
                        versionId: Joi.number().required(),
                    }),
                )
                .min(1)
                .required()
                .messages({
                    'array.min': 'Select at least one template.',
                }),
        }),
        otherwise: Joi.array()
            .items(
                Joi.object({
                    inventoryId: Joi.number().required(),
                    versionId: Joi.number().required(),
                }),
            )
            .optional()
            .default([]),
    }),
    templateTags: Joi.array().items(Joi.string().trim().min(1)).optional().default([]),
    templateTagsPaidOnly: Joi.boolean().optional().default(true),
});

export interface SextforceAutoRepostWithLatestRepostLog extends SextforceAutoRepost {
    color?: SextforceAutoRepostColor;
    latestRepostLog?: SextforceAutoRepostLog;
}

const useSextforceAutoRepost = (
    query: string,
    active: boolean | null,
    colorId: string | undefined | null,
    sortBy: '_id' | 'name' | 'description' | 'colorId' | 'nextRepostDate',
) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const axios = useAxios();
    const dialog = useDialog();
    const params = useParams();
    const queryClient = useQueryClient();

    const autoRepostsQueryKey = ['autoRepost', params.userId, userContext.jwtToken, query, active, colorId, sortBy];

    const [bulkDeleteAutoRepostsLoading, setBulkDeleteAutoRepostsLoading] = useState(false);
    const [bulkDistributeAutoRepostsLoading, setBulkDistributeAutoRepostsLoading] = useState(false);
    const [bulkAdjustAutoRepostsLoading, setBulkAdjustAutoRepostsLoading] = useState(false);
    const [bulkSetAutoRepostInactiveStatusLoading, setBulkSetAutoRepostInactiveStatusLoading] = useState(false);

    // Fetch online users count vs. total sales report
    const fetchData = async (
        query: string,
        active: boolean | null,
        colorId: string | undefined | null,
        sortBy: '_id' | 'name' | 'description' | 'colorId' | 'nextRepostDate',
    ): Promise<SextforceAutoRepostWithLatestRepostLog[]> => {
        if (!userContext.jwtToken || !settingsContext.apiKey) {
            return [];
        }

        const url: string = `${settingsContext.routes.autoRepost.base}${params.userId}?${new URLSearchParams({
            query: query.trim(),
            active: typeof active === 'boolean' ? (active ? 'true' : 'false') : 'null',
            colorId: colorId ? colorId : 'null',
            sortBy: sortBy,
        })}`;

        const data = await axios
            .get(url)
            .then(async response => response.data as SextforceAutoRepostWithLatestRepostLog[])
            .catch(error => {
                handleHttpError(error, dialog);

                return [];
            });

        return data;
    };

    const autoRepostsQuery = useQuery(
        autoRepostsQueryKey,
        () => {
            return fetchData(query, active, colorId, sortBy);
        },
        {
            refetchOnWindowFocus: false,
            // keep data while fetching new data
            keepPreviousData: true,
            // stale time 1 minute
            staleTime: 60000,
            enabled: userContext.jwtToken && params.userId && settingsContext.apiKey ? true : false,
        },
    );

    const fetchActiveAutoRepostsForBulkPause = async (): Promise<SextforceAutoRepostWithLatestRepostLog[]> => {
        return fetchData(query, true, colorId, sortBy);
    };

    const bulkDeleteAutoReposts = async (autoRepostIds: string[], active: boolean): Promise<{ success: boolean }> => {
        if (!userContext.jwtToken || !settingsContext.apiKey) {
            return { success: false };
        }

        const url: string = `${settingsContext.routes.autoRepost.base}${params.userId}/bulkDelete`;

        setBulkDeleteAutoRepostsLoading(true);

        const data = await axios
            .delete(url, {
                data: {
                    autoRepostIds,
                    active,
                },
            })
            .then(response => response.data)
            .catch(error => {
                handleHttpError(error, dialog);

                return { success: false };
            })
            .finally(() => {
                setBulkDeleteAutoRepostsLoading(false);
            });

        return data;
    };

    const bulkDistributeAutoReposts = async (
        autoRepostIds: string[],
        offsetAmount: number,
        offsetUnit: string,
        startDate: Date,
    ): Promise<{ success: boolean }> => {
        if (!userContext.jwtToken || !settingsContext.apiKey) {
            return { success: false };
        }

        const url: string = `${settingsContext.routes.autoRepost.base}${params.userId}/bulkDistribute`;

        setBulkDistributeAutoRepostsLoading(true);

        const data = await axios
            .post(url, {
                autoRepostIds,
                offsetAmount,
                offsetUnit,
                startDate,
            })
            .then(response => response.data)
            .catch(error => {
                handleHttpError(error, dialog);

                return { success: false };
            })
            .finally(() => {
                setBulkDistributeAutoRepostsLoading(false);
            });

        return data;
    };

    const bulkAdjustAutoReposts = async (
        autoRepostIds: string[],
        offsetAmount: number,
        offsetUnit: SextforceAutoRepostAdjustUnit,
    ): Promise<{ success: boolean; adjustedCount?: number }> => {
        if (!userContext.jwtToken || !settingsContext.apiKey) {
            return { success: false };
        }

        const url: string = `${settingsContext.routes.autoRepost.base}${params.userId}/bulkAdjust`;

        setBulkAdjustAutoRepostsLoading(true);

        const data = await axios
            .post(url, {
                autoRepostIds,
                offsetAmount,
                offsetUnit,
            })
            .then(response => response.data)
            .catch(error => {
                handleHttpError(error, dialog);

                return { success: false };
            })
            .finally(() => {
                setBulkAdjustAutoRepostsLoading(false);
            });

        return data;
    };

    const bulkSetAutoRepostInactiveStatus = async (autoRepostIds: string[], inactive: boolean): Promise<{ success: boolean }> => {
        if (!userContext.jwtToken || !settingsContext.apiKey) {
            return { success: false };
        }

        const url: string = `${settingsContext.routes.autoRepost.base}${params.userId}/bulkStatus`;

        setBulkSetAutoRepostInactiveStatusLoading(true);

        const data = await axios
            .patch(url, {
                autoRepostIds,
                inactive,
            })
            .then(response => {
                autoRepostsQuery.refetch();

                return response.data;
            })
            .catch(error => {
                handleHttpError(error, dialog);

                return { success: false };
            })
            .finally(() => {
                setBulkSetAutoRepostInactiveStatusLoading(false);
            });

        return data;
    };

    const setAutoRepostInactiveStatus = async (autoRepostId: string, inactive: boolean): Promise<{ success: boolean }> => {
        if (!userContext.jwtToken || !settingsContext.apiKey) {
            return { success: false };
        }

        const url: string = `${settingsContext.routes.autoRepost.base}${params.userId}/${autoRepostId}/status`;

        const data = await axios
            .patch(url, { inactive })
            .then(response => {
                const oldAutoReposts = queryClient.getQueryData<SextforceAutoRepostWithLatestRepostLog[]>(autoRepostsQueryKey);

                if (oldAutoReposts) {
                    queryClient.setQueryData<SextforceAutoRepostWithLatestRepostLog[]>(
                        autoRepostsQueryKey,
                        oldAutoReposts.map(autoRepost => (autoRepost._id === autoRepostId ? { ...autoRepost, inactive } : autoRepost)),
                    );
                }

                return response.data;
            })
            .catch(error => {
                handleHttpError(error, dialog);

                return { success: false };
            });

        return data;
    };

    return {
        ...autoRepostsQuery,
        bulkDeleteAutoReposts,
        bulkDeleteAutoRepostsLoading,
        bulkDistributeAutoReposts,
        bulkDistributeAutoRepostsLoading,
        bulkAdjustAutoReposts,
        bulkAdjustAutoRepostsLoading,
        bulkSetAutoRepostInactiveStatus,
        bulkSetAutoRepostInactiveStatusLoading,
        fetchActiveAutoRepostsForBulkPause,
        setAutoRepostInactiveStatus,
    };
};

export default useSextforceAutoRepost;
